import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ChevronRight, ChevronLeft, Sparkles, Music,
  TrendingUp, DollarSign, Flame, Trophy, Heart, Star,
  Users, Zap, MapPin, Clock, Crown,
} from 'lucide-react';

// ─── Spotify Wrapped-style monthly recap ────────────────────────────

interface Slide {
  bg: string;
  render: () => React.ReactNode;
}

const MonthlyWrapped: React.FC = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [animateIn, setAnimateIn] = useState(true);

  const goTo = (idx: number) => {
    setAnimateIn(false);
    setTimeout(() => {
      setCurrentSlide(idx);
      setAnimateIn(true);
    }, 200);
  };

  const next = () => { if (currentSlide < slides.length - 1) goTo(currentSlide + 1); };
  const prev = () => { if (currentSlide > 0) goTo(currentSlide - 1); };

  const slides: Slide[] = [
    // Slide 0: Intro
    {
      bg: 'from-violet-600 via-purple-600 to-fuchsia-600',
      render: () => (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <Sparkles className="w-12 h-12 text-purple-200 mb-6 animate-pulse" />
          <h1 className="text-5xl font-black text-white mb-3 tracking-tight">Your February</h1>
          <h2 className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-300 to-amber-300 mb-6">Wrapped</h2>
          <p className="text-purple-200 text-lg max-w-sm">Let's look back at everything you accomplished last month.</p>
          <div className="mt-12 text-purple-300 text-sm animate-bounce">Tap to continue</div>
        </div>
      ),
    },
    // Slide 1: Total earnings
    {
      bg: 'from-emerald-600 via-teal-600 to-cyan-600',
      render: () => (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <DollarSign className="w-10 h-10 text-emerald-200 mb-4" />
          <p className="text-emerald-200 text-sm uppercase tracking-widest mb-2">You earned</p>
          <h1 className="text-7xl font-black text-white mb-2">$2,847</h1>
          <p className="text-emerald-200 text-lg mb-8">in total earnings this month</p>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 max-w-xs w-full">
            <div className="flex justify-between items-center mb-3">
              <span className="text-emerald-100 text-sm">Base tips</span>
              <span className="text-white font-bold">$2,324</span>
            </div>
            <div className="flex justify-between items-center mb-3">
              <span className="text-emerald-100 text-sm">Upsell bonuses</span>
              <span className="text-amber-300 font-bold">+$523</span>
            </div>
            <div className="border-t border-white/20 pt-3 mt-3">
              <p className="text-emerald-200 text-xs">That's <span className="text-white font-bold">$503 more</span> than your first month with Bespoke</p>
            </div>
          </div>
        </div>
      ),
    },
    // Slide 2: Upsell personality
    {
      bg: 'from-orange-500 via-red-500 to-pink-500',
      render: () => (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <p className="text-orange-200 text-sm uppercase tracking-widest mb-4">Your upsell style is</p>
          <h1 className="text-5xl font-black text-white mb-2">The Storyteller</h1>
          <p className="text-orange-100 text-lg max-w-sm mb-8">
            You don't just suggest items — you paint a picture. Your descriptions are 2.3x longer than average, and guests love it.
          </p>
          <div className="grid grid-cols-2 gap-3 max-w-xs w-full">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-3xl font-black text-white">72%</div>
              <div className="text-orange-200 text-xs">Success rate</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-3xl font-black text-white">4.2s</div>
              <div className="text-orange-200 text-xs">Avg pitch length</div>
            </div>
          </div>
          <p className="text-orange-200 text-xs mt-6 italic max-w-xs">
            "Only 8% of servers nationwide share this style. It's the most effective for casual dining."
          </p>
        </div>
      ),
    },
    // Slide 3: Best moment
    {
      bg: 'from-amber-500 via-yellow-500 to-orange-400',
      render: () => (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <Trophy className="w-10 h-10 text-amber-200 mb-4" />
          <p className="text-amber-100 text-sm uppercase tracking-widest mb-2">Your best moment</p>
          <h1 className="text-4xl font-black text-white mb-4">February 8th</h1>
          <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 max-w-sm w-full text-left mb-6">
            <p className="text-white text-sm leading-relaxed">
              Saturday dinner rush. You hit a <span className="font-bold text-amber-200">9-interaction streak</span>,
              upsold Wagyu to a table of 6, and earned <span className="font-bold text-amber-200">$273</span> in a single shift.
            </p>
            <p className="text-amber-200 text-xs mt-3">Your best-earning shift ever.</p>
          </div>
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-amber-200" />
            <span className="text-amber-100 text-sm">You were in the top 3% of all servers that day</span>
          </div>
        </div>
      ),
    },
    // Slide 4: Growth
    {
      bg: 'from-blue-600 via-indigo-600 to-violet-600',
      render: () => (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <TrendingUp className="w-10 h-10 text-blue-200 mb-4" />
          <p className="text-blue-200 text-sm uppercase tracking-widest mb-2">Your growth</p>
          <h1 className="text-4xl font-black text-white mb-6">You're not the same server you were</h1>
          <div className="space-y-3 max-w-xs w-full">
            {[
              { label: 'Upsell rate', before: '58%', after: '72%', change: '+14%' },
              { label: 'Greeting score', before: '82%', after: '92%', change: '+10%' },
              { label: 'Avg tips/shift', before: '$156', after: '$203', change: '+$47' },
              { label: 'Rank', before: '#34', after: '#7', change: '+27 spots' },
            ].map((item, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-3 flex items-center justify-between">
                <span className="text-blue-200 text-sm">{item.label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-blue-300 text-sm line-through">{item.before}</span>
                  <ChevronRight className="w-3 h-3 text-blue-400" />
                  <span className="text-white font-bold text-sm">{item.after}</span>
                  <span className="text-emerald-300 text-xs font-medium">{item.change}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    // Slide 5: Superlatives
    {
      bg: 'from-pink-500 via-rose-500 to-red-500',
      render: () => (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <Star className="w-10 h-10 text-pink-200 mb-4" />
          <p className="text-pink-200 text-sm uppercase tracking-widest mb-4">Your superlatives</p>
          <div className="space-y-4 max-w-sm w-full">
            {[
              { title: 'Most Mentioned Item', value: 'Wagyu Platter', sub: 'You pitched it 47 times. 34 sold.' },
              { title: 'Favorite Shift', value: 'Saturday Dinner', sub: 'You averaged $241 on Saturday nights' },
              { title: 'Signature Move', value: '"Have you tried..."', sub: 'Your most successful opening line' },
              { title: 'Spirit Animal', value: 'The Golden Retriever', sub: 'Warm, enthusiastic, everyone\'s friend' },
              { title: 'Most Improved Metric', value: 'Drink Promotions', sub: 'From 2/shift to 6/shift. 200% growth.' },
            ].map((item, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-left">
                <div className="text-pink-300 text-[10px] uppercase tracking-widest mb-1">{item.title}</div>
                <div className="text-white font-bold text-lg">{item.value}</div>
                <div className="text-pink-200 text-xs">{item.sub}</div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    // Slide 6: Outro
    {
      bg: 'from-gray-900 via-gray-800 to-gray-900',
      render: () => (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <div className="text-6xl mb-6">
            <Crown className="w-16 h-16 text-amber-400 mx-auto" />
          </div>
          <h1 className="text-4xl font-black text-white mb-3">February was just the beginning.</h1>
          <p className="text-gray-400 text-lg max-w-sm mb-8">
            March is your month. Diamond tier is within reach. Your next record shift is out there waiting.
          </p>
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl p-5 max-w-xs w-full">
            <p className="text-amber-300 text-sm font-medium mb-1">March Challenge</p>
            <p className="text-white text-lg font-bold">Hit 80% upsell rate</p>
            <p className="text-amber-200/60 text-xs mt-1">Reward: 500 bonus points + Diamond badge</p>
          </div>
          <button
            onClick={() => navigate('/restaurant/mockups')}
            className="mt-8 px-6 py-3 bg-white text-gray-900 font-bold rounded-xl hover:bg-gray-100 transition-colors"
          >
            Back to Hub
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className={`min-h-screen bg-gradient-to-br ${slides[currentSlide].bg} transition-all duration-500 relative overflow-hidden`}>
      {/* Background decorative circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute -bottom-48 -left-48 w-[500px] h-[500px] rounded-full bg-black/10" />
        <div className="absolute top-1/3 right-1/4 w-64 h-64 rounded-full bg-white/5" />
      </div>

      {/* Back button */}
      <div className="absolute top-4 left-4 z-20">
        <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm transition-colors">
          <ArrowLeft className="w-4 h-4" /> Hub
        </button>
      </div>

      {/* Slide content */}
      <div
        className={`relative z-10 min-h-screen flex flex-col transition-all duration-300 ${
          animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        onClick={next}
      >
        {slides[currentSlide].render()}
      </div>

      {/* Navigation dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {currentSlide > 0 && (
          <button onClick={(e) => { e.stopPropagation(); prev(); }} className="p-1.5 text-white/40 hover:text-white transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        <div className="flex items-center gap-1.5">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); goTo(i); }}
              className={`rounded-full transition-all ${
                i === currentSlide ? 'w-6 h-2 bg-white' : 'w-2 h-2 bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>
        {currentSlide < slides.length - 1 && (
          <button onClick={(e) => { e.stopPropagation(); next(); }} className="p-1.5 text-white/40 hover:text-white transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default MonthlyWrapped;
